import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next"
import { colors, fonts, glyphs, useMediaQuery } from "../../../Styles"
import { AttentionCard, NewSelect, AttentionModal, SingleAttentionModal } from "../..";
import { AttentionTypes, ButtonTypes, LocaleFarmDate, NewFieldStyles, SelectFieldOption_Values } from "../../../Definitions";
import { Graph_QL, useAttentionLog, AttentionLog, System } from "../../../Services";
import NewButton from "../../BaseComponents/Buttons/newButton";

const ITEMS_PER_PAGE = 10;
interface Attentions_Props {
  height: string;
  system: System;
  connect: {
    domain: string;
    connection: Graph_QL.Connection;
  }
}

// #region | BannerBtns
const BannerBtns = memo(({ problemsTotal, problemText, warningsTotal, warningsText, onWarningClick, onProblemClick }: {
  problemsTotal: number, problemText: string, warningsTotal: number, warningsText: string, onWarningClick: ()=>void, onProblemClick: ()=>void
}) => (<>
  <NewButton
    content={`${problemsTotal} ${problemText}`}
    buttonType={ButtonTypes.textButton}
    onClick={onProblemClick}
    overrides={{
      contentStyles: {...fonts.fontSize4, color: colors.greyPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "90px"}
    }}
  />
  <p style={{ ...fonts.fontSize4, color: colors.greyPrimary }}>|</p>
  <NewButton
    content={`${warningsTotal} ${warningsText}`}
    buttonType={ButtonTypes.textButton}
    onClick={onWarningClick}
    overrides={{
      contentStyles: {...fonts.fontSize4, color: colors.greyPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "90px"}
    }}
  />
</>))
// #endregion

// #region | PageBtns
const PageBtns = memo(({ onNext, onPrev, currentPage, totalPages }: {
  onNext: () => void, onPrev: () => void, currentPage: number, totalPages: number
}) => (<>
  <NewButton
    content={""}
    buttonType={ButtonTypes.arrowButton}
    onClick={onPrev}
    disabled={currentPage === 1}
    startEnhancer={glyphs.Arrow({ rotate: 180 })}
    overrides={{
      baseStyles: { backgroundColor: colors.greyPrimary },
      hoverStyles: { backgroundColor: colors.greySecondary },
    }}
  />
  <NewButton
    content={""}
    buttonType={ButtonTypes.arrowButton}
    onClick={onNext}
    disabled={currentPage === totalPages || totalPages === 1}
    startEnhancer={glyphs.Arrow({})}
    overrides={{
      baseStyles: { backgroundColor: colors.greyPrimary },
      hoverStyles: { backgroundColor: colors.greySecondary },
    }}
  />
</>))
// #endregion

// #region | FilterSelect
const FilterSelect = memo(({selectedOption, onSelectOption}:{selectedOption:SelectFieldOption_Values | undefined, onSelectOption: (selectedOption: SelectFieldOption_Values | undefined) => void}) => {
  const { t } = useTranslation();
  
  const filterOptions = useMemo(() => [
    { label: t("systemControls.general.date", { defaultValue: "date" }), id: 0 },
    { label: t("systemControls.general.level", { defaultValue: "level" }), id: 1 },
    { label: t("systemControls.general.type", { defaultValue: "type" }), id: 2 },
  ], [t]);
  
  return(
  <NewSelect
    placeholder={t("filterBy", { defaultValue: "Filter By" })}
    options={filterOptions}
    onChange={onSelectOption}
    fieldOverrides={{
      fieldStyle: NewFieldStyles.greySecondary,
      fieldWidth: "100%"
    }}
    valueOverrides={{
      defaultValue: selectedOption ?? filterOptions[0]
    }}
  />)
});
// #endregion

/**
 * Attentions
 * @oaram height Widget height.
 * @param connect Farm, domain and grapghQL connection for attention log.
 * @returns A widget with AttentionCards based on pens with alert or warning in the system. Each card opens a Modal with more Pen informations.
 */
export default function Attentions({ height, connect, system }: Attentions_Props) {
  const { t } = useTranslation();
  const { systemId: urlSystemId } = useParams();
  
  // #region | Column Count
  const contentResizeRef = useRef<HTMLDivElement>(null)
  const [columns, setColumns] = useState(1);
  const smallContent = useMediaQuery(`(max-width: 1845px)`);

  useEffect(() => {
    const getColumnCount = () => {
      if (!smallContent) return 2;
      return 1;
    };
    setColumns(getColumnCount());
  }, [smallContent]);
  // #endregion

  // #region | Modals
  const [singleAttentionModal, setSingleAttentionModal] = useState<boolean>(false);
  const [attentionModal, setAttentionModal] = useState<boolean>(false);
  const [currentAttentionView, setCurrentAttentionView] = useState<"ALERTS" | "WARNINGS">("ALERTS");
  const [activeLogId, setActiveLogId] = useState<number | null>(null);
  // #endregion

  // #region | Filter Options
  const [selectedFilterOption, setSelectedFilterOption] = useState<SelectFieldOption_Values | undefined>();
  
  const handleSelectOption = useCallback((selectedOption: SelectFieldOption_Values | undefined) => {
    setSelectedFilterOption(selectedOption)
  }, []);
  // #endregion

  // #region | Data
  const { data: attentionLogState, call: getAttentionLogs, maintainEntries: dismissAttentionLog } = useAttentionLog(connect.connection);
  
  useEffect(() => {
    if (urlSystemId) {
      getAttentionLogs({ ids: [+urlSystemId] })
    }
  }, [urlSystemId])

  const logData = useMemo(() => {
    if (connect.domain in attentionLogState && attentionLogState[connect.domain] && typeof attentionLogState[connect.domain] === "object" && "data" in attentionLogState[connect.domain] && attentionLogState[connect.domain].data && urlSystemId) {
      return attentionLogState[connect.domain].data.filter((log) => log.fk_systems === +urlSystemId && log.dismissed !== true);
    }
    return []
  }, [attentionLogState, connect.domain, urlSystemId])
  // #endregion

  // #region | Filtering
  const logs = useMemo(() => {
    let data: AttentionLog[] = [...logData];
  
    if (data) {
      if (selectedFilterOption?.id === 1) {
        data.sort((a, b) => {
          if (a.level !== b.level) return a.level - b.level;
          return b.timestamp - a.timestamp;
        });
      } else if (selectedFilterOption?.id === 2) {
        data.sort((a, b) => {
          if (a.type !== b.type) return a.type - b.type;
          return b.timestamp - a.timestamp;
        });
      } else {
        data.sort((a, b) => b.timestamp - a.timestamp);
      }
    }
  
    return data;
  }, [logData, selectedFilterOption]);
  // #endregion

  // #region | Banner Content
  const bannerContent = useMemo(() => {
    let problemsTotal = logs.filter(pen => pen.level === 0).length;
    const problemText = problemsTotal === 1 ? t("systemControls.general.problem", { defaultValue: "Problem" }) : t("systemControls.general.problems", { defaultValue: "Problems" });
    let warningsTotal = logs.filter(pen => pen.level === 1).length;
    const warningsText = warningsTotal === 1 ? t("systemControls.general.warning", { defaultValue: "Warning" }) : t("systemControls.general.warnings", { defaultValue: "Warnings" });
    
    return {
      problemsTotal,
      problemText,
      warningsTotal,
      warningsText
    }
  }, [logs, t])

  const handleProblemClick = useCallback(() => {
    setAttentionModal(true);
    setCurrentAttentionView("ALERTS");
  }, []);
  
  const handleWarningClick = useCallback(() => {
    setAttentionModal(true);
    setCurrentAttentionView("WARNINGS");
  }, []);
  // #endregion

  // #region | Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    
    return logs.slice(startIndex, endIndex);
  }, [logs, currentPage]);

  // Total Pages
  const totalPages = useMemo(() => {
    if (paginatedLogs === undefined || logs.length === 0) {
      return 1
    } else {
      return Math.ceil(logs.length / ITEMS_PER_PAGE)
    }
  }, [paginatedLogs, logs.length]);
  
  const handleNextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  }, [totalPages]);
  
  const handlePrevPage = useCallback(() => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  }, []);
  // #endregion

  // #region | Card Object
  const handleLeftClick = useCallback((logId: number) => {
    setActiveLogId(logId);
    setSingleAttentionModal(true);
  }, []);
  
  const handleRightClick = useCallback((logId: number) => {
    if (dismissAttentionLog) dismissAttentionLog({ data: [logId] });
  }, [dismissAttentionLog]);

  const AttentionCards = useMemo(() => {
    return paginatedLogs?.map((log, index) => {
      return (
        <AttentionCard
          hover
          key={log.attention_log_id}
          onLeftClick={() => handleLeftClick(log.attention_log_id)}
          onRightClick={() => handleRightClick(log.attention_log_id)}
          attentionType={log.level === 0 ? AttentionTypes.alert : AttentionTypes.warning}
          systemContent={
            log.location_name ?
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between" }}>
                <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{`Pen: ${log.location_name}`}</p>
              </div>
              : undefined
          }
          problemContent={<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", width: "100%", textAlign: "center" }}>
            <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{log.message}</p>
            <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{new LocaleFarmDate(log.timestamp * 1000).toLocaleFarmString()}</p>
          </div>}
        />
      )
        ;
    })
  }, [handleLeftClick, handleRightClick, paginatedLogs]);
  //#endregion
  
  return ( <>
    <div style={{height: height, width: "100%"}}>
      <div style={{width: "100%", height: "calc(100% - 35px)", marginBottom: "5px", backgroundColor: colors.greyLightTertiary, borderRadius: "10px", border: "5px solid "+colors.greyPrimary, boxSizing: "border-box", display: "grid", gridTemplateColumns: "1fr", gridTemplateRows: "90px 30px 1fr"}}>
        <div style={{ gridArea: "1/1/2/2", width: "100%", height: "100%", display: "flex", flexDirection: "column", padding: "0 10px", alignItems: "center", justifyContent: "center", backgroundColor: colors.greyPrimary, boxSizing: "border-box", gap: "10px" }}>
          <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary, display: "flex", alignItems: "center"}}>{t("systemControls.general.attentions", {defaultValue: "Attentions"})}</p>
          <div style={{maxWidth: "280px", width: "100%", zIndex: 9}}>
            <FilterSelect onSelectOption={handleSelectOption} selectedOption={selectedFilterOption}/>
          </div>
        </div>
        <div style={{ gridArea: "2/1/3/2", display: "flex", alignItems: "center", gap: "5px", justifyContent: columns > 1 ? "left" : "center", padding: columns > 1 ? "0px 20px" : "0px", backgroundColor: colors.greyTertiary, height: "30px", ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary, boxSizing: "border-box"}}>
          <BannerBtns
            problemsTotal={bannerContent.problemsTotal}
            problemText={bannerContent.problemText}
            warningsTotal={bannerContent.warningsTotal}
            warningsText={bannerContent.warningsText}
            onProblemClick={handleProblemClick}
            onWarningClick={handleWarningClick}
          />
      </div>
        <div ref={contentResizeRef} style={{gridArea: "3/1/4/2", padding: "10px", width: "100%", overflowY: "auto", overflowX: "hidden", height: "100%", boxSizing: "border-box", scrollbarColor: colors.greyTertiary+colors.transparent, scrollbarWidth: "thin"}}>
          <div style={{columnCount: columns, columnGap: "10px" }}>
            {AttentionCards}
          </div>
        </div>
      </div>

      <div style={{display: "flex", alignItems: "center", gap: "5px", padding: "0px 20px" }}>
        <PageBtns currentPage={currentPage} totalPages={totalPages} onNext={handleNextPage} onPrev={handlePrevPage}/>
        <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary }}>
          {`${t("page", { defaultValue: "page" })} ${currentPage} ${t("of", { defaultValue: "of" })} ${totalPages}`}
        </p>
      </div>
    </div>
    
    {/* Modal for Attention Log */}
    {(singleAttentionModal && activeLogId && urlSystemId) ?
      <SingleAttentionModal system={system} modalState={!!singleAttentionModal} onClose={() => setSingleAttentionModal(false)} systemId={urlSystemId} clickedLog={activeLogId}/>
    : <></>}
    
    {/* Modal for Attentions View */}
    {(attentionModal && urlSystemId) ?
      <AttentionModal modalState={!!attentionModal} onClose={() => setAttentionModal(false)} systemId={urlSystemId} attentionview={currentAttentionView}/>
    : <></>}
  </> )
}